/**
 * Guard a whole page on a feature flag. If the tenant has the feature
 * disabled, render an empty state instead of the page — that's kinder
 * than a 404 or a broken screen if the user hits the URL directly (e.g.
 * from a bookmark or a link in an old email).
 */

import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { OwnerLayout } from '@/components/OwnerLayout';
import { useFeature, type FeatureKey } from './hooks';

interface Props {
  feature: FeatureKey;
  featureLabel: string;
  children: React.ReactNode;
}

export function FeatureGate({ feature, featureLabel, children }: Props) {
  const enabled = useFeature(feature);
  if (enabled) return <>{children}</>;

  return (
    <OwnerLayout>
      <div className="mx-auto max-w-lg py-16 text-center">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-muted text-muted-foreground">
          <Lock className="h-5 w-5" />
        </div>
        <div className="text-lg font-semibold">{featureLabel} is turned off</div>
        <p className="mt-2 text-sm text-muted-foreground">
          This feature isn't enabled for your pump. If you'd like to use it, contact your
          FuelSync administrator.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block text-sm font-medium text-primary hover:underline"
        >
          Back to dashboard
        </Link>
      </div>
    </OwnerLayout>
  );
}
